import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { Button } from '../ui/button';

const COLS = 20;
const ROWS = 16;
const TICK_MS = 140;

const randomFood = (snake) => {
  let cell;
  do {
    cell = { x: Math.floor(Math.random() * COLS), y: Math.floor(Math.random() * ROWS) };
  } while (snake.some((s) => s.x === cell.x && s.y === cell.y));
  return cell;
};

export function SnakeGame({ onScore }) {
  const [snake, setSnake] = useState([{ x: 8, y: 8 }]);
  const [food, setFood] = useState({ x: 14, y: 8 });
  const [score, setScore] = useState(0);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const dirRef = useRef({ x: 1, y: 0 });

  const turn = useCallback((x, y) => {
    const d = dirRef.current;
    if (d.x === -x && d.y === -y) return;
    dirRef.current = { x, y };
  }, []);

  useEffect(() => {
    const map = {
      ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0],
      w: [0, -1], s: [0, 1], a: [-1, 0], d: [1, 0]
    };
    const handler = (e) => {
      const move = map[e.key];
      if (move) {
        e.preventDefault();
        turn(move[0], move[1]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [turn]);

  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => {
      setSnake((current) => {
        const d = dirRef.current;
        const head = { x: current[0].x + d.x, y: current[0].y + d.y };
        const hitWall = head.x < 0 || head.y < 0 || head.x >= COLS || head.y >= ROWS;
        const hitSelf = current.some((s) => s.x === head.x && s.y === head.y);
        if (hitWall || hitSelf) {
          setRunning(false);
          setOver(true);
          return current;
        }
        const ate = head.x === food.x && head.y === food.y;
        const next = [head, ...(ate ? current : current.slice(0, -1))];
        if (ate) {
          setScore((s) => s + 10);
          setFood(randomFood(next));
        }
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [running, food]);

  useEffect(() => {
    if (over && score > 0) onScore(score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over]);

  const start = () => {
    const initial = [{ x: 8, y: 8 }];
    setSnake(initial);
    setFood(randomFood(initial));
    dirRef.current = { x: 1, y: 0 };
    setScore(0);
    setOver(false);
    setRunning(true);
  };

  return (
    <div className="space-y-4" data-testid="game-snake">
      <div className="flex items-center justify-between text-sm">
        <span>Score: <span className="text-amber-300 tabular-nums" data-testid="snake-score">{score}</span></span>
        <span className="text-xs text-muted-foreground">Arrows / WASD or pad below</span>
      </div>

      <div
        className="relative w-full rounded-lg border border-white/10 bg-black/60 overflow-hidden"
        style={{ aspectRatio: `${COLS} / ${ROWS}` }}
        data-testid="snake-board"
      >
        {snake.map((cell, i) => (
          <div
            key={`${cell.x}-${cell.y}-${i}`}
            className={`absolute ${i === 0 ? 'bg-amber-300' : 'bg-emerald-400/80'} rounded-sm`}
            style={{ left: `${(cell.x / COLS) * 100}%`, top: `${(cell.y / ROWS) * 100}%`, width: `${100 / COLS}%`, height: `${100 / ROWS}%` }}
          />
        ))}
        <div
          className="absolute bg-rose-500 rounded-full"
          style={{ left: `${(food.x / COLS) * 100}%`, top: `${(food.y / ROWS) * 100}%`, width: `${100 / COLS}%`, height: `${100 / ROWS}%` }}
        />
        {!running && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <span className="text-sm" data-testid="snake-overlay">
              {over ? `Game over — ${score} points` : 'Press Start to play'}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => turn(0, -1)} data-testid="snake-up"><ArrowUp size={16} /></Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => turn(-1, 0)} data-testid="snake-left"><ArrowLeft size={16} /></Button>
          <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => turn(0, 1)} data-testid="snake-down"><ArrowDown size={16} /></Button>
          <Button variant="outline" size="sm" className="rounded-full w-12" onClick={() => turn(1, 0)} data-testid="snake-right"><ArrowRight size={16} /></Button>
        </div>
      </div>

      <Button variant="outline" className="rounded-full w-full" onClick={start} data-testid="snake-start">
        {running ? 'Restart' : 'Start'}
      </Button>
    </div>
  );
}
